import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import { usePlanner } from '../../hooks/PlannerContext';
import { useSharedState } from '../../hooks/SharedStateContext';
import { fetchExistingPlanner, plannerSignature, triggerPlannerCreation } from '../../utilities/lessonPlanning';
import { getCurrentUserName, getUserMemory, hasNotificationPreferences } from '../../utilities/firebase';
import { getNextLearningPathActivity, isLearningPlanComplete } from '../../utilities/activitySelection';
import { getUrlParameter } from '../../utilities/urlUtils';
import ActivityCard from './ActivityCard';
import LearningProgressBar from './LearningProgressBar';
import PlanCompleteCard from './PlanCompleteCard';
import ReminderSetupCard from './ReminderSetupCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faGraduationCap, faArrowLeft, faEdit, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import * as Sentry from '@sentry/react';
import { trackEvent } from '../../utilities/tracking';
import './ActivityList.css';
import '../../pages/ObjectivePage.css';

const POLL_INTERVAL_MS = 10000;
const TIMEOUT_MS = 90 * 1000; // 90 seconds — don't leave users waiting forever
const STALE_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes — clear stale pending state
const LOADING_MESSAGE_COUNT = 6;
const LOADING_MESSAGE_INTERVAL_MS = 5000;

function clearPendingFlags() {
  localStorage.removeItem('plannerPending');
  localStorage.removeItem('plannerPendingObjective');
  localStorage.removeItem('plannerPendingTargetLanguage');
  localStorage.removeItem('plannerPendingStartTime');
  localStorage.removeItem('plannerPendingPreviousSignature');
}

// A pending regeneration is only "ready" once the latest planner differs from
// the plan that existed when generation started — otherwise the poll would
// accept the old plan and the new one would never show without a reload.
function isPendingPlanReady(planner) {
  if (!planner || !(planner.number_activities > 0)) return false;
  const previousSignature = localStorage.getItem('plannerPendingPreviousSignature') || '';
  return !previousSignature || plannerSignature(planner) !== previousSignature;
}

function ObjectiveView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const plannerContext = usePlanner();
  const {
    currentPlannerData,
    setCurrentPlannerData,
    handlePlannerActivitySelect,
    handlePlannerGoalEdit,
  } = plannerContext || {};
  const { handleActivities } = useSharedState();
  const { width, height } = useWindowSize();

  const [pageState, setPageState] = useState('init'); // init | input | loading | planReady | ready | timeout
  const [objective, setObjective] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const pollRef = useRef(null);
  const timeoutRef = useRef(null);
  const loadingMsgRef = useRef(null);
  const creationTriggeredRef = useRef(false);

  const hasReminders = hasNotificationPreferences();

  // Cycle loading messages
  useEffect(() => {
    if (pageState !== 'loading') return;
    loadingMsgRef.current = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % LOADING_MESSAGE_COUNT);
    }, LOADING_MESSAGE_INTERVAL_MS);
    return () => { if (loadingMsgRef.current) clearInterval(loadingMsgRef.current); };
  }, [pageState]);

  const getLoadingMessage = () => {
    const key = `objective_generating_${loadingMessageIndex + 1}`;
    return t(key, { defaultValue: t('objective_generating') });
  };

  // Polling logic for pending planners
  const startPolling = useCallback(() => {
    const startTime = parseInt(localStorage.getItem('plannerPendingStartTime') || Date.now().toString(), 10);
    const remaining = TIMEOUT_MS - (Date.now() - startTime);

    if (remaining <= 0) {
      setPageState('timeout');
      clearPendingFlags();
      return;
    }

    const poll = async () => {
      try {
        const planner = await fetchExistingPlanner();
        if (isPendingPlanReady(planner)) {
          if (setCurrentPlannerData) setCurrentPlannerData(planner);
          clearPendingFlags();
          setPageState('planReady');
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          return;
        }
      } catch (err) {
        // Keep polling
      }
      pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();

    timeoutRef.current = setTimeout(async () => {
      if (pollRef.current) clearTimeout(pollRef.current);
      // A regenerated plan can share the old plan's signature — before
      // declaring a timeout, settle on whatever latest plan exists.
      try {
        const planner = await fetchExistingPlanner();
        if (planner && planner.number_activities > 0) {
          if (setCurrentPlannerData) setCurrentPlannerData(planner);
          clearPendingFlags();
          setPageState('planReady');
          return;
        }
      } catch (err) {
        // Fall through to the timeout state
      }
      clearPendingFlags();
      setPageState('timeout');
      Sentry.captureMessage('Planner generation timed out after polling', {
        level: 'warning',
        tags: { feature: 'planner', action: 'polling_timeout' },
      });
    }, Math.max(remaining, 0));
  }, [setCurrentPlannerData]);

  // Shared creation flow: set pending flags, trigger creation, fall back to polling
  const startCreation = (objectiveText) => {
    const pendingTargetLanguage = localStorage.getItem('plannerPendingTargetLanguage')
      || localStorage.getItem('targetLanguage')
      || 'fr';

    setObjective(objectiveText);
    setPageState('loading');
    setLoadingMessageIndex(0);
    localStorage.setItem('plannerPending', 'true');
    localStorage.setItem('plannerPendingObjective', objectiveText);
    localStorage.setItem('plannerPendingTargetLanguage', pendingTargetLanguage);
    localStorage.setItem('plannerPendingStartTime', Date.now().toString());
    // Remember what the plan looked like before regeneration so polling can
    // tell the old plan apart from the new one (empty when no plan exists).
    localStorage.setItem('plannerPendingPreviousSignature', plannerSignature(currentPlannerData));

    triggerPlannerCreation({
      objective: objectiveText,
      name: getCurrentUserName(),
      level: localStorage.getItem('userLevel'),
      age: localStorage.getItem('userAge'),
      language: localStorage.getItem('userLanguage'),
      targetLanguage: pendingTargetLanguage,
      memory: getUserMemory() || undefined,
    }).then((planningData) => {
      if (planningData && planningData.number_activities > 0) {
        if (setCurrentPlannerData) setCurrentPlannerData(planningData);
        clearPendingFlags();
        setPageState('planReady');
        return;
      }
      startPolling();
    });
  };

  // Retry: the plan may have finished saving after we stopped polling (generation
  // can outlast our window), so check for an existing plan first and only
  // regenerate if there still isn't one — otherwise "Try again" does nothing useful.
  const handleRetry = async () => {
    const pendingObjective = objective || localStorage.getItem('plannerPendingObjective') || '';
    setObjective(pendingObjective);
    setPageState('loading');
    try {
      const planner = await fetchExistingPlanner();
      if (isPendingPlanReady(planner)) {
        if (setCurrentPlannerData) setCurrentPlannerData(planner);
        clearPendingFlags();
        setPageState('planReady');
        return;
      }
    } catch (err) {
      // No existing plan available — fall through to regeneration.
    }
    startCreation(pendingObjective);
  };

  // Submit a new objective from the input form
  const handleSubmitObjective = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    startCreation(trimmed);
  };

  // On mount: determine state
  useEffect(() => {
    // DEV: ?mock=loading to seed localStorage and trigger real planner creation
    const mockState = getUrlParameter('mock');
    if (mockState === 'loading') {
      // Seed localStorage for mock — creationTriggeredRef prevents StrictMode double-fire
      const mockObjective = 'Learn French for professional conversations in 3 months';
      localStorage.setItem('plannerPending', 'true');
      localStorage.setItem('plannerPendingObjective', mockObjective);
      localStorage.setItem('plannerPendingTargetLanguage', 'fr');
      localStorage.removeItem('plannerPendingStartTime');
      localStorage.removeItem('notificationPreferences');
      // Fall through to the normal pending flow below
    }

    const isPending = localStorage.getItem('plannerPending') === 'true';

    if (isPending) {
      // Clear stale pending state from previous sessions — let the user resubmit
      const existingStartTime = localStorage.getItem('plannerPendingStartTime');
      if (existingStartTime && (Date.now() - parseInt(existingStartTime, 10)) > STALE_THRESHOLD_MS) {
        console.log('🗺️ Clearing stale pending planner state (older than 10 minutes)');
        setInputValue(localStorage.getItem('plannerPendingObjective') || '');
        clearPendingFlags();
        setPageState('input');
        return;
      }

      const pendingObjective = localStorage.getItem('plannerPendingObjective') || '';
      const pendingTargetLanguage = localStorage.getItem('plannerPendingTargetLanguage') || 'fr';
      setObjective(pendingObjective);
      setPageState('loading');

      // Only trigger creation once — if no startTime exists yet, this is the first visit
      // Ref guard prevents StrictMode double-fire
      if (!localStorage.getItem('plannerPendingStartTime') && !creationTriggeredRef.current) {
        creationTriggeredRef.current = true;
        localStorage.setItem('plannerPendingStartTime', Date.now().toString());
        triggerPlannerCreation({
          objective: pendingObjective,
          name: getCurrentUserName(),
          level: localStorage.getItem('userLevel'),
          age: localStorage.getItem('userAge'),
          language: localStorage.getItem('userLanguage'),
          targetLanguage: pendingTargetLanguage,
          memory: getUserMemory() || undefined,
        }).then((planningData) => {
          if (planningData && planningData.number_activities > 0) {
            // POST returned the planning data directly — skip polling
            if (setCurrentPlannerData) setCurrentPlannerData(planningData);
            clearPendingFlags();
            setPageState('planReady');
            return;
          }
          // No data in response — fall back to polling
          startPolling();
        });
      } else {
        startPolling();
      }
    } else if (currentPlannerData && currentPlannerData.number_activities > 0) {
      // Already have planner data from PlannerContext
      setPageState('ready');
    } else {
      // Try to fetch — no planner means the user still needs to set an objective,
      // so show the input form instead of silently bouncing home
      (async () => {
        try {
          const planner = await fetchExistingPlanner();
          if (planner && planner.number_activities > 0) {
            if (setCurrentPlannerData) setCurrentPlannerData(planner);
            setPageState('ready');
          } else {
            setInputValue(localStorage.getItem('plannerPendingObjective') || '');
            setPageState('input');
          }
        } catch (err) {
          Sentry.captureException(err, {
            tags: { feature: 'planner', action: 'objective_fetch_failed' },
          });
          setInputValue(localStorage.getItem('plannerPendingObjective') || '');
          setPageState('input');
        }
      })();
    }

    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const plannerData = currentPlannerData;
  const planComplete = isLearningPlanComplete(plannerData);

  const handleActivityClick = (activityValue) => {
    if (handlePlannerActivitySelect) {
      handlePlannerActivitySelect(activityValue);
      navigate('/');
    }
  };

  const handleContinueLearningPath = () => {
    if (!plannerData) return;
    const activityValue = getNextLearningPathActivity(plannerData);
    if (activityValue) {
      handleActivityClick(activityValue);
    }
  };

  const handleExamLearningPath = () => {
    handleActivityClick('exam-learning-path');
  };

  const handleBack = () => {
    if (handleActivities) handleActivities();
    navigate('/');
  };

  const handleEditGoal = () => {
    if (handlePlannerGoalEdit && plannerData) {
      handlePlannerGoalEdit(plannerData.goal);
    }
    navigate('/');
  };

  if (pageState === 'init') {
    return (
      <div className="objective-content">
        <div className="objective-spinner">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  const displayObjective = objective.length > 120
    ? objective.substring(0, 120) + '...'
    : objective;

  const showConfetti = pageState === 'loading' || pageState === 'planReady';

  return (
    <>
      {showConfetti && createPortal(
        <Confetti
          width={width}
          height={height}
          recycle={false}
          numberOfPieces={300}
          className="objective-confetti"
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999, pointerEvents: 'none', border: 'none' }}
        />,
        document.body
      )}

      {/* Input state — no planner yet, let the user write an objective */}
      {pageState === 'input' && (
        <div className="objective-content">
          <div className="objective-card">
            <h2 className="objective-title">
              {t('planner_question')}
            </h2>
            <form
              className="objective-form"
              onSubmit={(e) => { e.preventDefault(); handleSubmitObjective(); }}
            >
              <textarea
                className="objective-textarea"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={t('planner_description')}
                aria-label={t('planner_question')}
                rows={4}
              />
              <div className="objective-form-actions">
                <button type="submit" className="objective-cta" disabled={!inputValue.trim()}>
                  {t('submit_objective')}
                </button>
                <button type="button" className="objective-cta-secondary" onClick={handleBack}>
                  {t('back')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Loading state — pending planner */}
      {pageState === 'loading' && (
        <div className="objective-content">
          <div className="objective-card" role="status" aria-live="polite">
            <div className="objective-spinner">
              <div className="loader"></div>
            </div>
            <h2 className="objective-title">
              {getLoadingMessage()}
            </h2>
            {displayObjective && (
              <p className="objective-text">
                {displayObjective}
              </p>
            )}
            <p className="objective-hint">
              {t('planner_edit_hint', "You'll be able to edit this goal anytime.")}
            </p>
          </div>
          {!hasReminders && <ReminderSetupCard />}
        </div>
      )}

      {/* Plan ready — show reveal button before full plan */}
      {pageState === 'planReady' && plannerData && (
        <div className="objective-content">
          <div className="objective-card objective-card-ready">
            <div className="objective-check" aria-hidden="true">&#10003;</div>
            <h2 className="objective-title">
              {t('plan_ready_title', 'Your learning plan is ready!')}
            </h2>
            <p className="objective-text">
              {t('plan_ready_description', 'We created {{count}} personalized activities based on your conversation.', { count: plannerData.number_activities || 20 })}
            </p>
            <button type="button" className="objective-cta" onClick={() => {
              trackEvent('madameai_plan_revealed', { source: 'objective_page', activities: plannerData.number_activities });
              setPageState('ready');
            }}>
              {t('show_learning_plan', 'Show me my learning plan')}
            </button>
          </div>
        </div>
      )}

      {/* Ready state — full plan view */}
      {pageState === 'ready' && plannerData && (
        <div className="activity-buttons">
          <div className="planner-nav-row">
            <button className="ghost-button" onClick={handleBack}>
              <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '0.5em' }} />
              {t('back')}
            </button>
            <button className="ghost-button" onClick={handleEditGoal}>
              <FontAwesomeIcon icon={faEdit} style={{ marginRight: '0.5em' }} />
              {t('edit_goal')}
            </button>
          </div>

          <div className="goal-section">
            <h2>{t('your_learning_goal')}</h2>
            <div className="goal-display">
              <p>{plannerData.goal}</p>
            </div>

            {/* Plan finished: point users at a new goal instead of a dead end */}
            {planComplete ? (
              <PlanCompleteCard
                activityCount={plannerData.number_activities}
                onTakeEvaluation={handleExamLearningPath}
                onCreateNewPlan={handleEditGoal}
                onPracticeAgain={handleContinueLearningPath}
              />
            ) : (
              <button
                className="button activity planner-continue-button continue-glow"
                onClick={handleContinueLearningPath}
              >
                <FontAwesomeIcon icon={faPlay} style={{ marginRight: '0.5em' }} />
                {t('continue_learning_path')}
              </button>
            )}

            {/* The completion card already leads with the evaluation */}
            {!planComplete && (
              <button className="button activity" onClick={handleExamLearningPath}>
                <FontAwesomeIcon icon={faGraduationCap} style={{ marginRight: '0.5em' }} />
                {t('test_learning_path')}
              </button>
            )}
          </div>

          <LearningProgressBar plannerData={plannerData} showLabel={true} />

          <div className="planner-activities-grid">
            {Object.entries(plannerData)
              .filter(([, value]) => typeof value === 'object' && value !== null)
              .map(([, { activityId, title, description, progress }]) => (
                <ActivityCard
                  key={`learning-path-${activityId}`}
                  activityId={activityId}
                  title={title}
                  description={description}
                  progress={progress}
                  onActivityClick={handleActivityClick}
                />
              ))}
          </div>
        </div>
      )}

      {/* Timeout state */}
      {pageState === 'timeout' && (
        <div className="objective-content">
          <div className="objective-card">
            <h2 className="objective-title">
              {t('objective_timeout', 'Plan generation took too long. You can create one from the main app.')}
            </h2>
            <div className="objective-timeout-actions">
              <button type="button" className="objective-cta" onClick={handleRetry}>
                <FontAwesomeIcon icon={faRotateRight} style={{ marginRight: '0.5em' }} />
                {t('try_again', 'Try again')}
              </button>
              <button type="button" className="objective-cta-secondary" onClick={handleBack}>
                {t('continue', 'Continue')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ObjectiveView;
